import { observer } from "mobx-react-lite";
import MainLayout, { MAIN_RIGHT_TOP_HEIGHT } from "../MainLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { HorizontalLayout, VerticalLayout } from "../../common/common-layout";
import { TitleLargeMedium, TitleMediumBold } from "../../common/common-text";
import BTBox from "../../common/components/BTBox";
import { useEffect, useMemo, useState } from "react";
import Post from "../../model/archive/Post";
import { formatDateToYMD } from "../../common/utils/DateUtils";
import { dialogStore } from '../../store/DialogStore';
import { Button, Divider } from "@mui/material";
import BTButton from "../../common/components/BTButton";
import theme from '../../common/theme';
import { fetchPost, deletePost } from "../../common/firebase/Firestore";
import { ref, deleteObject } from "firebase/storage";
import { storage } from "../../common/firebase/Firebase";
import './../../css/detail.css'
import BTInputBox from "../../common/components/BTInputBox";
import BTMultilineHTMLReader from "../../common/components/board/BTMultilineHTMLReader";

type BoardDetailProps = {
    boardId: string;
    boardName: string;
};

const BoardDetail = ({boardId, boardName}: BoardDetailProps) => {
    return (
        <MainLayout>
            <VerticalLayout>
                <BTBox height="20px" />
                <BoardDetailBody boardId={boardId} boardName={boardName} />
            </VerticalLayout>
        </MainLayout>
    );
};

export default BoardDetail;

const BoardDetailBody = observer(({boardId, boardName}: BoardDetailProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [post, setPost] = useState<Post>();
    const pageUrl = window.location.href;
    const title = "SNS 공유 기능 샘플";
    const description = "React에서 SNS 공유 기능을 구현하는 예제입니다.";

    function extractFileLinksFromHtml(html: string): string[] {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const links: string[] = [];

        // <a href="..."> 추출
        // doc.querySelectorAll('a[href]').forEach((a) => {
        //     const href = a.getAttribute('href');
        //     if (href) links.push(href);
        // });

        // <img src="..."> 추출
        doc.querySelectorAll('img[src]').forEach((img) => {
            const src = img.getAttribute('src');
            if (src) links.push(src);
        });

        return links;
    }

    const forceDownload = async (url: string, filename: string) => {
        try {
            const response = await fetch(url, { mode: 'cors' });
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("다운로드 실패:", error);
            window.open(url, "_blank");
        }
    };

    const handleDownloadAll = async () => {
        for (let i = 0; i < attachedFiles.length; i++) {
            const url = attachedFiles[i];
            const filename = decodeURIComponent(url.split('/').pop() || `첨부파일${i + 1}`);
            await forceDownload(url, filename); // ✅ 앞서 만든 함수 재사용
        }
    };

    const reportPost = async () => {

    }

    const attachedFiles = useMemo(() => {
        const contentLinks = post?.contents ? extractFileLinksFromHtml(post.contents) : [];
        const fileLinks = post?.attachedFiles ?? [];
        return [...contentLinks, ...fileLinks];
    }, [post]);

    useEffect(() => {
        const loadPost = async () => {
            const statePost = location.state as Post | undefined;
            if (statePost?.id) {
                const data = await fetchPost(boardId, statePost.id);
                if (data) {
                    setPost(data);
                }
            }
        };

        loadPost();
    }, [location.state]);

    const processedHtml = useMemo(() => {
        if (!post?.contents) return '';
        // <img> 태그에 스타일 추가
        return post.contents.replace(/<img\s/gi, '<img style="max-width: 100%; height: auto;" ');
    }, [post?.contents]);

    // Facebook 공유
    const shareFacebook = () => {
        const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        window.open(fbUrl, "_blank");
    };

    // Twitter 공유
    const shareTwitter = () => {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(pageUrl)}`;
        window.open(twitterUrl, "_blank");
    };

    // LinkedIn 공유
    const shareLinkedIn = () => {
        const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        window.open(linkedInUrl, "_blank");
    };

    // KakaoTalk 공유 (Kakao SDK 필요)
    const shareKakao = () => {
        /*
        if (window.Kakao) {
            const Kakao = window.Kakao;
            if (!Kakao.isInitialized()) {
                Kakao.init("YOUR_KAKAO_APP_KEY"); // 🔹 여기에 카카오 앱 키를 넣어야 함
            }

            Kakao.Share.sendDefault({
                objectType: "feed",
                content: {
                    title: title,
                    description: description,
                    imageUrl: "https://via.placeholder.com/300",
                    link: {
                        mobileWebUrl: pageUrl,
                        webUrl: pageUrl,
                    },
                },
            });
        }
            */
    };

    const iconButtonStyle = {
        width: '32px',
        height: '32px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '18px',
        border: 'none',
        borderRadius: '6px',
        color: 'white',
        backgroundColor: '#333', // 기본 배경 (개별 버튼에서 override 가능)
        cursor: 'pointer',
        margin: '0 4px',
    };

    const handleEdit = () => {
        navigate(`/board/${boardId}/edit`, { state: post })
    };

    const deleteFileFromStorage = async (filePath: string): Promise<any> => {
        const storageRef = ref(storage, filePath);
        await deleteObject(storageRef);
        console.log(`파일 삭제 완료: ${filePath}`);
    };

    const handleDelete = () => {
        if (post != null) {
            dialogStore.showDialog({
                title: '알림',
                message: '정말로 삭제 하시겠습니까?',
                onConfirm: async () => {
                    try {
                        await Promise.all(
                            attachedFiles.map((file, index) => {
                                deleteFileFromStorage(file);
                            }));

                        await deletePost(boardId, post!.id!)
                    } catch (e) {
                        dialogStore.showDialog({
                            title: '에러',
                            message: '삭제 실패 하였습니다.',
                            onConfirm: () => { },
                        });
                    }

                    dialogStore.showDialog({
                        title: '알림',
                        message: '삭제 완료 하였습니다.',
                        onConfirm: () => {
                            navigate(-1);
                         },
                    });
                },
            });
        }
    };

    const handleToList = () => {
        navigate(-1);
    };

    return (
        <div style={{ width: '100%', minHeight: `calc(100vh - ${MAIN_RIGHT_TOP_HEIGHT})`, display: 'flex', flexDirection: 'column' }} >
            <div style={{ flex: 1, overflowY: 'auto' }}>
                <VerticalLayout height={'auto'} paddingLeft={36} paddingRight={36}>
                    <TitleLargeMedium>{boardName}</TitleLargeMedium>
                    <BTBox height='24px' />
                    {post && (
                        <VerticalLayout height={'auto'} paddingBottom={36}>
                            <div
                                style={{
                                    display: "flex",
                                    gap: "16px",
                                    marginTop: "8px",
                                    marginBottom: "16px",
                                    fontSize: "20px",
                                    color: "#6C757D",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                }}
                            >
                                <b>제목</b>
                                <div>{post.title}</div>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    gap: "16px",
                                    fontSize: "14px",
                                    color: "#6C757D",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                }}
                            >
                                <b>작성자</b>
                                <div>{post.createdBy || '알 수 없음'}</div>
                                <Divider style={{ width: "1px", height: "13px", backgroundColor: "#6C757D" }} />
                                <b>작성일</b>
                                <div>{post.createdAt ? formatDateToYMD(post.createdAt) : '-'}</div>
                            </div>
                            <BTBox height='20px' />
                            <BTMultilineHTMLReader
                                html={processedHtml}
                                borderColor="#adb5bd"
                                minHeight="250px"
                                width="calc(100% - 50px)" // 기존 marginRight: '50px' 대체
                            />
                            {/* 첨부 파일 */}
                            { attachedFiles.length > 0 && (
                                <div style={{ marginTop: '24px' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                                        {/* 제목 */}
                                        <div
                                            style={{
                                                minWidth: '64px',
                                                fontSize: '14px',
                                                color: '#6C757D',
                                                lineHeight: '28px', // 제목과 텍스트 높이 정렬
                                            }}
                                        >
                                            <b>첨부파일</b>
                                        </div>

                                        {/* 파일 목록 */}
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '8px',
                                                flex: 1,
                                            }}
                                        >
                                            {attachedFiles.map((file, index) => {
                                                const splitUrl = file.split('?')[0];
                                                const cleanUrl = decodeURIComponent(splitUrl.substring(splitUrl.lastIndexOf('/') + 1));
                                                const filename = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1);

                                                return (
                                                    <span
                                                        key={index}
                                                        onClick={() => forceDownload(file, filename)}
                                                        title={filename}
                                                        style={{
                                                            fontSize: '14px',
                                                            color: '#6C757D',
                                                            cursor: 'pointer',
                                                            textDecoration: 'underline',
                                                            whiteSpace: 'normal',
                                                            overflow: 'visible',
                                                            textOverflow: 'unset',
                                                            wordBreak: 'break-all',
                                                            lineHeight: '28px',
                                                            display: 'block',
                                                            maxWidth: '100%'
                                                        }}
                                                    >
                                                        {filename}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </VerticalLayout>
                    )}
                </VerticalLayout>
            </div>
        </div>
    );
});
